import { memo, useEffect, useRef, useState } from 'react'
import { ReactComponent as IconCalendar } from './assets/calendar.svg'
import dayjs from 'dayjs'
import { uniq } from 'lodash'
import {
  UpdatesWrap,
  UpdatesPageTitle,
  UpdatesMain,
  UpdatesLeft,
  UpdatesYears,
  UpdatesYearFirst,
  UpdatesYearsDotSmall,
  UpdatesYear,
  UpdatesYearsDot,
  UpdatesYearLast,
  UpdatesVersions,
  UpdatesVersionList,
  UpdatesVersionListItem,
  UpdatesVersionNumber,
  UpdatesVersionLatest,
  UpdatesVersionDate,
  UpdatesRight,
  UpdatesRightTitle,
  UpdatesArticle,
  UpdatesArticleTop,
  UpdatesArticleVersion,
  UpdatesArticleDate,
  UpdatesArticleTitle,
  UpdatesArticleContent,
} from './styles'
import { scrollToElementById } from 'utils/scrollToElementById'
import { Updates } from 'constants/updates'

function getActiveIndex(articles: HTMLDivElement[]) {
  for (let i = 0; i < articles.length; i++) {
    const article = articles[i]

    const bounding = article.getBoundingClientRect()

    if (bounding.top >= 0) {
      return i
    }
  }

  return undefined
}
export const UpdatesView = memo(() => {
  const years = uniq(Updates.map((update) => dayjs(update.time).year()))

  const articleRefs = useRef<HTMLDivElement[]>([])

  const versionBox = useRef<HTMLDivElement>(null)
  const versionRefs = useRef<HTMLLIElement[]>([])

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const articles = articleRefs.current

      const ai = getActiveIndex(articles)
      if (ai === undefined) {
        return
      }

      setActiveIndex(ai)

      versionRefs.current[ai].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const currentUpdate = Updates[activeIndex]
  const currentYear = dayjs(currentUpdate.time).year()

  return (
    <UpdatesWrap>
      <UpdatesPageTitle>Product Updates</UpdatesPageTitle>

      <UpdatesMain>
        <UpdatesLeft>
          <UpdatesYears>
            <UpdatesYearFirst>
              <UpdatesYearsDotSmall />
            </UpdatesYearFirst>
            {years.map((year) => (
              <UpdatesYear
                key={year}
                onClick={() => {
                  const i = Updates.findIndex(
                    (v) => dayjs(v.time).year() === year,
                  )
                  versionRefs.current[i].click()
                }}
              >
                {year}
                <UpdatesYearsDot $active={currentYear === year} />
              </UpdatesYear>
            ))}
            <UpdatesYearLast>
              <UpdatesYearsDotSmall />
            </UpdatesYearLast>
          </UpdatesYears>
          <UpdatesVersions
            ref={versionBox}
            onMouseEnter={(event) => {
              event.currentTarget.style.overflow = 'auto'
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.overflow = 'hidden'
            }}
          >
            <h3>Release Timeline</h3>
            <UpdatesVersionList>
              {Updates?.map((update, index) => (
                <UpdatesVersionListItem
                  ref={(ref) =>
                    (versionRefs.current[index] = ref as HTMLLIElement)
                  }
                  key={update.version}
                  $active={currentUpdate.version === update.version}
                  onClick={() => {
                    scrollToElementById(`update-article-${update.version}`)
                  }}
                >
                  <UpdatesVersionNumber>
                    {update.version}
                    {index === 0 && (
                      <UpdatesVersionLatest>Latest</UpdatesVersionLatest>
                    )}
                  </UpdatesVersionNumber>
                  <UpdatesVersionDate>
                    {dayjs(update.time).format('MMM-DD-YYYY')}
                  </UpdatesVersionDate>
                </UpdatesVersionListItem>
              ))}
            </UpdatesVersionList>
          </UpdatesVersions>
        </UpdatesLeft>
        <UpdatesRight>
          <UpdatesRightTitle>Release notes</UpdatesRightTitle>

          {Updates?.map((update, index) => (
            <UpdatesArticle
              id={`update-article-${update.version}`}
              ref={(ref) =>
                (articleRefs.current[index] = ref as HTMLDivElement)
              }
              key={update.version}
            >
              <UpdatesArticleTop>
                <UpdatesArticleVersion>
                  Version {update.version}
                </UpdatesArticleVersion>
                <UpdatesArticleDate>
                  <IconCalendar />
                  {dayjs(update.time).format('MMM DD, YYYY')}
                </UpdatesArticleDate>
              </UpdatesArticleTop>
              <UpdatesArticleTitle>{update.title}</UpdatesArticleTitle>
              <UpdatesArticleContent>
                {update.content.map((content, index) => (
                  <li key={index}>{content}</li>
                ))}
              </UpdatesArticleContent>
            </UpdatesArticle>
          ))}
        </UpdatesRight>
      </UpdatesMain>
    </UpdatesWrap>
  )
})
